import * as joint from '@joint/core';
import { EdgeRoutingMode, LayerDirectionEnum, layout as msaglLayout } from '@joint/layout-msagl';
import { applyConnectorStyleMixin, getFlowTypeInfo } from './constants';
import { createEntityNode } from './entity-nodes';
import { Callout, DrawnLine, DrawnRectangle, IconElement, TextBox } from './drawable-elements';
import './flow-links';
import { createFlowLink, createFlowLinkTextLabel } from './flow-links';
import { createGraph, getLinkEndId, type EntityCatalogEntry } from './graph-load';
import { collapseCoincidentFlows, expandMergedFlows, linkGroupFor, ownedStepIds } from './flow-merge';
import {
    applyFlowElementPatch,
    applyFlowLinkPatch,
    clearAutoLayoutHint,
    requireFinitePoint,
    requirePositiveSize,
    setElementGeometry,
    updateLinkTextLabel,
    type FlowElementPatchDescriptor,
    type FlowLinkPatchDescriptor,
    type PatchValue,
    type PointValue,
    type SizeValue,
} from './graph-actions';
import { isLiteralFlowEndpoint } from './capability-registry';
import { isIconLabel } from './icons';
import { FlowType, Jurisdiction, ShapeType, EntityNodeOptions } from './types';
import { Node } from './node-base';
import { applyOrgChartStylingToNode } from './org-chart-styling';

// Re-exported so existing consumers (tests, fixtures) keep importing it from here; the
// definition moved to graph-load.ts alongside the shared graph loader.
export type { EntityCatalogEntry } from './graph-load';

// Default node dimensions, shared by the individual-entity and group-box creation paths so the
// sizes stay consistent in one place rather than being repeated as literals at each call site.
const ENTITY_NODE_WIDTH = 170;
const ENTITY_NODE_HEIGHT = 100;
const GROUP_NODE_WIDTH = 170;
const GROUP_NODE_HEIGHT = 100;

// ─── Batch API ───────────────────────────────────────────────────────────────

// Mirrors the C# FlowGraphMutation records (IFlowGraphMutator.cs), which serialize themselves
// polymorphically into this union — the "type" values are the [JsonDerivedType] discriminators.
// `shape` is a canonical shape name resolved by EntityShapeResolver in C# (matching the ShapeType
// enum member names); the classification→shape decision is never made on this side.
export type MutationDescriptor =
    | {
        type: 'ensureEntityNode'; entityId: string; shape: string; countryCode: string; label: string;
        isNewEntity: boolean; forceStandalone?: boolean; layoutExistingAgentNode?: boolean; isExternal?: boolean;
        position?: PointValue; diagramType?: string; country?: string;
    }
    | { type: 'removeEntityNode'; entityId: string }
    | { type: 'addFlowLink'; sourceEntityId: string; targetEntityId: string; flowType: FlowType; stepId?: string; label?: string }
    | { type: 'removeStepLinks'; stepId: string }
    | { type: 'reconcileStepProjection'; stepId: string; desiredLinks: StepProjectionLinkDescriptor[]; isOpaque?: boolean }
    | {
        type: 'createEntityGroupWithIndividuals'; entities: EntityCatalogEntry[]; groupLabel?: string;
        position?: PointValue;
    }
    | {
        type: 'addFlowElement';
        reference: string;
        elementKind: 'TextBox' | 'Callout' | 'Rectangle' | 'Icon' | 'Line' | 'ThirdParty';
        geometry: FlowElementGeometryDescriptor;
        patch?: FlowElementPatchDescriptor;
        entityId?: string;
    }
    | { type: 'updateFlowElement'; cellId: string; patch: FlowElementPatchDescriptor }
    | { type: 'removeFlowElement'; cellId: string }
    | {
        type: 'addLiteralFlow';
        source: FlowEndpointReferenceDescriptor;
        target: FlowEndpointReferenceDescriptor;
        flowType: FlowType;
        patch?: FlowLinkPatchDescriptor;
        stepId?: string;
    }
    | { type: 'updateFlow'; cellId: string; patch: FlowLinkPatchDescriptor }
    | { type: 'removeFlow'; cellId: string; expectedStepIds: Array<string | null> }
    | {
        type: 'removeFlowBySemantic'; sourceEntityId: string; targetEntityId: string; flowType: FlowType;
        allowedOwnerStepId: string;
    };

export interface FlowElementGeometryDescriptor {
    position?: PointValue;
    size?: SizeValue;
    tailTip?: PointValue;
    sourcePoint?: PointValue;
    targetPoint?: PointValue;
}

export interface FlowEndpointReferenceDescriptor {
    entityId?: string;
    cellId?: string;
    addedElementRef?: string;
}

export interface StepProjectionLinkDescriptor {
    sourceEntityId: string;
    targetEntityId: string;
    flowType: FlowType;
    label?: PatchValue<string>;
}

export interface ApplyMutationsInGraphJsonRequest {
    graphJson: string;
    mutations: MutationDescriptor[];
    entityCatalog?: EntityCatalogEntry[];
}

// State threaded through one mutation batch: the graph being mutated, the entity catalog for
// resolving names/shapes/colours, request-local references to elements whose persisted ids were
// generated by JointJS, the group boxes this batch touched (only those are candidates for
// single-member normalisation — untouched boxes are never rewritten), and layout tracking.
interface BatchContext {
    graph: joint.dia.Graph;
    catalog: Map<string, EntityCatalogEntry>;
    addedElements: Map<string, joint.dia.Cell>;
    touchedBoxIds: Set<string>;
    layoutTracking: LayoutTracking;
}

export async function applyMutationsToGraphJson(request: ApplyMutationsInGraphJsonRequest): Promise<string> {
    const ctx: BatchContext = {
        graph: createGraph(request.graphJson),
        catalog: buildEntityCatalog(request.entityCatalog),
        addedElements: new Map<string, joint.dia.Cell>(),
        touchedBoxIds: new Set<string>(),
        layoutTracking: createLayoutTracking(),
    };
    const { graph, layoutTracking } = ctx;

    // Coincident flows persist as one arrow carrying the rest as metadata (flow-merge.ts). Give
    // each of them its own link for the duration of this batch so the group/split primitives below
    // — which follow a flow by its recorded endpoint entities — keep working on one flow at a
    // time, then re-collapse before serialising. Caller-supplied cell ids still address the drawn
    // arrow; see requireFlowLinkGroup.
    expandMergedFlows(graph);

    for (const mutation of request.mutations) {
        switch (mutation.type) {
            case 'ensureEntityNode': {
                // The mutation carries the entity's current metadata; make it available to every
                // catalog-driven path in this batch (e.g. relabelling a box it is split out of).
                registerCatalogEntries(ctx.catalog, [mutation]);
                const node = upsertEntityNode(ctx, mutation);
                if (node) {
                    layoutTracking.addedNodeIds.add(String(node.id));
                }
                break;
            }
            case 'removeEntityNode': {
                removeEntity(ctx, mutation.entityId);
                break;
            }
            case 'addFlowLink': {
                const link = addFlowLinkToGraph(ctx, mutation);
                if (link) {
                    layoutTracking.addedLinkIds.add(String(link.id));
                }
                break;
            }
            case 'removeStepLinks': {
                const targetId = mutation.stepId.toLowerCase();
                const linksToRemove = graph.getLinks().filter(link => {
                    const sid = link.prop('stepId');
                    return typeof sid === 'string' && sid.toLowerCase() === targetId;
                });
                graph.removeCells(linksToRemove);
                break;
            }
            case 'reconcileStepProjection': {
                const addedLinks = reconcileStepProjection(ctx, mutation);
                for (const link of addedLinks) {
                    layoutTracking.addedLinkIds.add(String(link.id));
                }
                break;
            }
            case 'createEntityGroupWithIndividuals': {
                registerCatalogEntries(ctx.catalog, mutation.entities);
                // Draws a single group box (no member cards); it lays out like any other node.
                const groupNode = createEntityGroup(ctx, mutation);
                if (groupNode) {
                    layoutTracking.addedNodeIds.add(String(groupNode.id));
                }
                break;
            }
            case 'addFlowElement': {
                const addedNode = await addFlowElement(ctx, mutation);
                if (addedNode) {
                    layoutTracking.addedNodeIds.add(String(addedNode.id));
                }
                break;
            }
            case 'updateFlowElement': {
                const cell = requireCell(graph, mutation.cellId);
                if (cell instanceof joint.dia.Link && !(cell instanceof DrawnLine)) {
                    throw new Error(`Cell '${mutation.cellId}' is a flow; use updateFlow.`);
                }
                await applyFlowElementPatch(cell, mutation.patch);
                break;
            }
            case 'removeFlowElement': {
                removeFlowElement(graph, mutation.cellId);
                break;
            }
            case 'addLiteralFlow': {
                const link = await addLiteralFlow(ctx, mutation);
                layoutTracking.addedLinkIds.add(String(link.id));
                break;
            }
            case 'updateFlow': {
                // Patch every flow the addressed arrow draws, not just the one whose cell carries
                // its id: they are identical by construction, so patching one would both under-
                // apply the caller's edit and desynchronise the group into separate arrows.
                for (const link of requireFlowLinkGroup(graph, mutation.cellId)) {
                    await applyFlowLinkPatch(link, mutation.patch);
                }
                break;
            }
            case 'removeFlow': {
                removeFlow(graph, mutation);
                break;
            }
            case 'removeFlowBySemantic': {
                removeFlowBySemantic(graph, mutation);
                break;
            }
        }
    }

    // A group box that this batch has split/merged down to a single member is no longer a group:
    // redraw it as that entity's own individual shape. Only boxes the batch touched are visited —
    // pre-existing boxes (user-created or from earlier batches) are never rewritten by a batch
    // that did not concern them.
    normalizeSingleMemberGroups(ctx);

    // Links whose endpoint entities now share one node are hidden (not deleted); links whose
    // endpoints separated again are re-shown.
    updateSameNodeLinkVisibility(graph);

    // Fold coincident flows (same source, target, type, labels, and connector style) onto one
    // survivor cell before layout runs, so a folded flow is never itself routed. See flow-merge.ts.
    collapseCoincidentFlows(graph);

    // After batch mutations, arrange all agent-added nodes and route their links.
    applyMsaglLayout(graph, layoutTracking);

    return serializeGraph(graph);
}

// ─── Entity catalog ───────────────────────────────────────────────────────────

function buildEntityCatalog(entries: EntityCatalogEntry[] | undefined): Map<string, EntityCatalogEntry> {
    const catalog = new Map<string, EntityCatalogEntry>();
    registerCatalogEntries(catalog, entries ?? []);
    return catalog;
}

function registerCatalogEntries(
    catalog: Map<string, EntityCatalogEntry>,
    entries: ReadonlyArray<{ entityId: string; label: string; shape: string; countryCode: string; isExternal?: boolean }>,
): void {
    for (const entry of entries) {
        if (!catalog.has(entry.entityId)) {
            catalog.set(entry.entityId, {
                entityId: entry.entityId,
                label: entry.label,
                shape: entry.shape,
                countryCode: entry.countryCode,
                isExternal: entry.isExternal,
            });
        }
    }
}

// ─── Invariant primitives ─────────────────────────────────────────────────────

// The one shared primitive for taking an entity out of whatever node currently hosts it:
// - a node with other entities left releases just this id — a group box is relabelled and
//   recoloured from the catalog, while any other shared node (e.g. a user-drawn multi-entity
//   third-party card) keeps its visuals untouched, since the engine did not author them;
// - a last-entity node has its unattributed links rescued onto `moveOrphanedLinksTo` (when the
//   caller supplies a new home) and is then removed.
// Links attributed to the entity are NOT handled here — callers move those with
// reattachEntityLinks, which follows the entity precisely by its recorded endpoint ids.
function releaseEntityFromNode(ctx: BatchContext, node: Node, entityId: string, moveOrphanedLinksTo?: Node): void {
    const ids: string[] = Array.isArray(node.entityIds) ? node.entityIds : [];
    const remaining = ids.filter(id => id !== entityId);
    if (remaining.length > 0) {
        // { rewrite: true } because joint's prop() merges arrays by index instead of replacing,
        // which would otherwise leave a stale trailing element when the array shrinks.
        node.prop('entityIds', remaining, { rewrite: true });
        if (node.get('type') === 'EntityGroup') {
            ctx.touchedBoxIds.add(String(node.id));
            relabelGroupBox(ctx, node);
        }
        return;
    }
    if (moveOrphanedLinksTo) {
        moveConnectedLinks(ctx.graph, node, moveOrphanedLinksTo);
    }
    removeNodeAndConnectedLinks(ctx.graph, node);
}

// Reattaches every flow link whose recorded endpoint entity is `entityId` so that endpoint points
// at `toNode` (the node that now hosts the entity). This is how split/merge/ungroup preserve arrows
// precisely: each link records the member entity ids it runs between (see addFlowLink), so a box
// holding several members can be taken apart without guessing which link belongs to which member —
// every link simply follows its own endpoint entity to that entity's new node. Links that predate
// this attribution (no recorded entity id) are handled separately by the orphaned-link rescue.
function reattachEntityLinks(
    graph: joint.dia.Graph,
    entityId: string,
    toNode: Node,
    candidateLinks: joint.dia.Link[] = graph.getLinks(),
): void {
    const toId = String(toNode.id);
    for (const link of candidateLinks) {
        if (String(link.prop('sourceEntityId') ?? '') === entityId && getLinkEndId(link, 'source') !== toId) {
            link.source(toNode, { selector: 'body' });
        }
        if (String(link.prop('targetEntityId') ?? '') === entityId && getLinkEndId(link, 'target') !== toId) {
            link.target(toNode, { selector: 'body' });
        }
    }
}

function indexLinksByEntityId(graph: joint.dia.Graph): Map<string, joint.dia.Link[]> {
    const index = new Map<string, joint.dia.Link[]>();
    const add = (entityId: unknown, link: joint.dia.Link) => {
        if (typeof entityId !== 'string' || entityId.length === 0) {
            return;
        }
        const list = index.get(entityId);
        if (list) {
            list.push(link);
        } else {
            index.set(entityId, [link]);
        }
    };
    for (const link of graph.getLinks()) {
        add(link.prop('sourceEntityId'), link);
        add(link.prop('targetEntityId'), link);
    }
    return index;
}

// Removes a node and the links connected to it. Shared by the explicit removeEntityNode mutation
// and the group-release path so both clean up identically.
function removeNodeAndConnectedLinks(graph: joint.dia.Graph, node: Node): void {
    graph.removeCells([node, ...graph.getConnectedLinks(node)]);
}

function moveConnectedLinks(graph: joint.dia.Graph, fromNode: Node, toNode: Node): void {
    const fromId = String(fromNode.id);
    for (const link of graph.getConnectedLinks(fromNode)) {
        if (getLinkEndId(link, 'source') === fromId) {
            link.source(toNode, { selector: 'body' });
        }
        if (getLinkEndId(link, 'target') === fromId) {
            link.target(toNode, { selector: 'body' });
        }
    }
}

// A link whose endpoints currently land on the same node — both its endpoint entities live in one
// group box — is kept (attribution, type, and label intact) but hidden: a box self-loop renders as
// noise, yet deleting the link would silently lose the flow. When a later split/ungroup separates
// the endpoints, the link is re-shown. 'root' is joint's implicit selector for the cell view's
// root element, so the hide survives serialisation and applies in the editor too.
function updateSameNodeLinkVisibility(graph: joint.dia.Graph): void {
    for (const link of graph.getLinks()) {
        const sourceId = getLinkEndId(link, 'source');
        const targetId = getLinkEndId(link, 'target');
        const sameNode = sourceId !== undefined && sourceId === targetId;
        if (sameNode && link.prop('hiddenSameNodeLink') !== true) {
            link.prop('hiddenSameNodeLink', true);
            link.attr('root/display', 'none');
        } else if (!sameNode && link.prop('hiddenSameNodeLink') === true) {
            link.removeProp('hiddenSameNodeLink');
            link.removeAttr('root/display');
        }
    }
}

// ─── Mutation handlers ────────────────────────────────────────────────────────

function requireCell(graph: joint.dia.Graph, cellId: string): joint.dia.Cell {
    const cell = graph.getCell(cellId);
    if (!cell) {
        throw new Error(`Cell '${cellId}' does not exist.`);
    }
    return cell;
}

function requireFlowLink(graph: joint.dia.Graph, cellId: string): joint.shapes.standard.Link {
    const cell = requireCell(graph, cellId);
    if (!(cell instanceof joint.shapes.standard.Link) || cell instanceof DrawnLine) {
        throw new Error(`Cell '${cellId}' is not a flow link.`);
    }
    return cell;
}

// Every flow the arrow `cellId` draws. A cell id only ever names a drawn arrow (inspection reports
// nothing else), so an operation on it belongs to all of them — mid-batch that is the addressed
// cell plus whatever expandMergedFlows restored from it.
function requireFlowLinkGroup(graph: joint.dia.Graph, cellId: string): joint.dia.Link[] {
    return linkGroupFor(graph, requireFlowLink(graph, cellId));
}

function requirePositionAndSize(
    geometry: FlowElementGeometryDescriptor,
    elementKind: string,
): { position: PointValue; size: SizeValue } {
    if (!geometry.position || !geometry.size) {
        throw new Error(`${elementKind} requires position and size.`);
    }
    requireFinitePoint(geometry.position, `${elementKind} position`);
    requirePositiveSize(geometry.size, `${elementKind} size`);
    return { position: geometry.position, size: geometry.size };
}

// Places a newly created entity-like node (entity, group box, or Third Party). Without an explicit
// position it is stamped `agentAdded` and dropped at the default placement for automatic layout to
// arrange; with one it keeps the author's coordinates and never joins the layout pool. Returns
// true when the node was auto-placed.
function placeNode(node: Node, position: PointValue | undefined, graph: joint.dia.Graph): boolean {
    if (position) {
        requireFinitePoint(position, 'position');
        node.position(position.x, position.y);
        return false;
    }
    node.prop('agentAdded', true);
    const placement = getDefaultEntityPlacement(graph);
    node.position(placement.x, placement.y);
    return true;
}

// Moves an existing entity-like node to an author-chosen position, demoting its automatic-layout
// hint so a later batch's layout pass leaves the shape where it was put.
function repositionNode(node: Node, position: PointValue): void {
    requireFinitePoint(position, 'position');
    node.position(position.x, position.y);
    clearAutoLayoutHint(node);
}

async function addFlowElement(
    ctx: BatchContext,
    mutation: Extract<MutationDescriptor, { type: 'addFlowElement' }>,
): Promise<Node | undefined> {
    const { graph } = ctx;
    const reference = mutation.reference?.trim();
    if (!reference) {
        throw new Error('A new flow element requires a non-empty request-local reference.');
    }
    const referenceKey = reference.toLowerCase();
    if (ctx.addedElements.has(referenceKey)) {
        throw new Error(`Added-element reference '${reference}' is duplicated.`);
    }

    let cell: joint.dia.Cell;
    let layoutNode: Node | undefined;
    switch (mutation.elementKind) {
        case 'TextBox': {
            const { position, size } = requirePositionAndSize(mutation.geometry, mutation.elementKind);
            cell = new TextBox({ position, size });
            break;
        }
        case 'Callout': {
            const { position, size } = requirePositionAndSize(mutation.geometry, mutation.elementKind);
            cell = new Callout({ position, size });
            // With no authored target, retain the Callout class's native default tail exactly as
            // the manual draw path does. A resolved target arrives as an explicit graph point.
            if (mutation.geometry.tailTip) {
                setElementGeometry(cell, 'tailTip', mutation.geometry.tailTip);
            }
            break;
        }
        case 'Rectangle': {
            const { position, size } = requirePositionAndSize(mutation.geometry, mutation.elementKind);
            cell = new DrawnRectangle({ position, size });
            break;
        }
        case 'Icon': {
            const { position, size } = requirePositionAndSize(mutation.geometry, mutation.elementKind);
            cell = new IconElement({ position, size });
            break;
        }
        case 'Line': {
            if (!mutation.geometry.sourcePoint || !mutation.geometry.targetPoint) {
                throw new Error('Line requires sourcePoint and targetPoint.');
            }
            cell = new DrawnLine();
            setElementGeometry(cell, 'sourcePoint', mutation.geometry.sourcePoint);
            setElementGeometry(cell, 'targetPoint', mutation.geometry.targetPoint);
            break;
        }
        case 'ThirdParty': {
            // Position is the one geometry an author supplies: size stays engine-owned, and the
            // tail/point fields belong to the annotation kinds that have them.
            if (mutation.geometry.size || mutation.geometry.tailTip
                || mutation.geometry.sourcePoint || mutation.geometry.targetPoint) {
                throw new Error('ThirdParty accepts position only; its size is owned by the engine.');
            }
            const node = createEntityNode(ShapeType.ThirdParty);
            if (mutation.entityId) {
                const entry = ctx.catalog.get(mutation.entityId);
                if (!entry || entry.isExternal !== true) {
                    throw new Error(`External entity '${mutation.entityId}' does not exist.`);
                }
                const existingHosts = graph.getElements().filter(element =>
                    element instanceof Node
                    && Array.isArray(element.entityIds)
                    && element.entityIds.includes(mutation.entityId!),
                );
                if (existingHosts.length > 0) {
                    throw new Error(`Entity '${mutation.entityId}' is already present in the diagram.`);
                }
                node.entityIds = [mutation.entityId];
                node.setLabel(entry.label);
            } else {
                node.entityIds = [];
                node.setLabel('Third Party');
            }
            node.setColorByJurisdiction(Jurisdiction.ThirdParty);
            node.resize(ENTITY_NODE_WIDTH, ENTITY_NODE_HEIGHT);
            if (placeNode(node, mutation.geometry.position, graph)) {
                // Auto-placed: joins the layout pool so MSAGL arranges it with the batch.
                layoutNode = node;
            }
            cell = node;
            break;
        }
        default:
            throw new Error(`Unsupported flow element kind '${String((mutation as any).elementKind)}'.`);
    }

    graph.addCell(cell);
    await applyFlowElementPatch(cell, mutation.patch);
    ctx.addedElements.set(referenceKey, cell);
    return layoutNode;
}

function removeFlowElement(graph: joint.dia.Graph, cellId: string): void {
    const cell = requireCell(graph, cellId);
    if (cell instanceof joint.shapes.standard.Link && !(cell instanceof DrawnLine)) {
        throw new Error(`Cell '${cellId}' is a flow; use removeFlow.`);
    }
    if (cell instanceof joint.dia.Element) {
        graph.removeCells([cell, ...graph.getConnectedLinks(cell)]);
        return;
    }
    graph.removeCells([cell]);
}

function removeFlow(
    graph: joint.dia.Graph,
    mutation: Extract<MutationDescriptor, { type: 'removeFlow' }>,
): void {
    const link = requireFlowLink(graph, mutation.cellId);
    const representedLinks = linkGroupFor(graph, link);
    const actualStepIds = distinctSortedStepIds(representedLinks.map(flow => {
        const stepId = flow.prop('stepId');
        return stepId === null || stepId === undefined ? null : String(stepId);
    }));
    const expectedStepIds = distinctSortedStepIds(mutation.expectedStepIds);
    if (JSON.stringify(actualStepIds) !== JSON.stringify(expectedStepIds)) {
        throw new Error(
            `Flow '${mutation.cellId}' step owners changed: expected ${JSON.stringify(expectedStepIds)}, `
            + `found ${JSON.stringify(actualStepIds)}.`,
        );
    }

    // Removing an arrow removes every flow it draws — the caller deleted what they could see, and
    // leaving the rest behind would silently redraw a replacement arrow in its place.
    graph.removeCells(representedLinks);
}

// Removes flows by (source, target, type) for callers with no cell id. Merged arrows are expanded
// before mutations run, so each constituent is matched independently and surviving constituents
// are re-collapsed afterward. Only visual-only flows or flows owned solely by the allowed step are
// eligible for removal.
function removeFlowBySemantic(
    graph: joint.dia.Graph,
    mutation: Extract<MutationDescriptor, { type: 'removeFlowBySemantic' }>,
): void {
    const linkClassName = getFlowTypeInfo(mutation.flowType).linkClassName;
    const allowedOwner = mutation.allowedOwnerStepId.toLowerCase();

    const matches = graph.getLinks().filter(link => {
        if (link.attributes.type !== linkClassName) {
            return false;
        }
        if (!endpointMatches(link, 'source', mutation.sourceEntityId)
            || !endpointMatches(link, 'target', mutation.targetEntityId)) {
            return false;
        }
        // Unowned (visual-only) or owned solely by the requesting step. Anything else belongs to
        // another step and is not this caller's to remove.
        return ownedStepIds(link).every(stepId => stepId.toLowerCase() === allowedOwner);
    });

    // No match is an ordinary outcome: the superseded flow may never have been drawn.
    if (matches.length > 0) {
        graph.removeCells(matches);
    }
}

// Identity per endpoint, matching the graph engine and reconcileStepProjection: an attributed
// endpoint must match by entity id — inside a group box every member link shares the same endpoint
// nodes, so node membership alone cannot tell A→B apart from C→D — while a legacy endpoint without
// attribution falls back to the node's entity membership.
function endpointMatches(link: joint.dia.Link, end: 'source' | 'target', entityId: string): boolean {
    return projectionEndpointEntityIds(link, end)?.includes(entityId) ?? false;
}

function distinctSortedStepIds(stepIds: Array<string | null>): Array<string | null> {
    const unique = new Map<string, string | null>();
    for (const stepId of stepIds) {
        const normalized = stepId === null ? null : stepId.toLowerCase();
        unique.set(normalized === null ? 'absent' : `present:${normalized}`, normalized);
    }
    return [...unique.values()].sort((left, right) => {
        if (left === null) return right === null ? 0 : -1;
        if (right === null) return 1;
        return left.localeCompare(right);
    });
}

interface ResolvedLiteralEndpoint {
    element: joint.dia.Element;
    entityId?: string;
}

function resolveLiteralEndpoint(
    ctx: BatchContext,
    reference: FlowEndpointReferenceDescriptor,
    role: 'source' | 'target',
): ResolvedLiteralEndpoint {
    const hasEntityId = typeof reference.entityId === 'string' && reference.entityId.length > 0;
    const hasCellId = typeof reference.cellId === 'string' && reference.cellId.length > 0;
    const hasAddedElementRef = typeof reference.addedElementRef === 'string' && reference.addedElementRef.length > 0;
    if (Number(hasEntityId) + Number(hasCellId) + Number(hasAddedElementRef) !== 1) {
        throw new Error(`${role} must specify exactly one of entityId, cellId, or addedElementRef.`);
    }

    let element: joint.dia.Element;
    let entityId: string | undefined;
    if (hasEntityId) {
        const matches = ctx.graph.getElements().filter(candidate =>
            candidate instanceof Node
            && Array.isArray(candidate.entityIds)
            && candidate.entityIds.includes(reference.entityId!),
        );
        if (matches.length !== 1) {
            throw new Error(`${role} entity '${reference.entityId}' resolved to ${matches.length} diagram cells.`);
        }
        element = matches[0];
        entityId = reference.entityId;
    } else if (hasCellId) {
        const cell = requireCell(ctx.graph, reference.cellId!);
        if (!(cell instanceof joint.dia.Element)) {
            throw new Error(`${role} cell '${reference.cellId}' is not an element.`);
        }
        element = cell;
        if (element instanceof Node && Array.isArray(element.entityIds) && element.entityIds.length === 1) {
            entityId = element.entityIds[0];
        }
    } else {
        const addedReference = reference.addedElementRef!.trim();
        const cell = ctx.addedElements.get(addedReference.toLowerCase());
        if (!cell) {
            throw new Error(`${role} added-element reference '${addedReference}' was not found earlier in this batch.`);
        }
        if (!(cell instanceof joint.dia.Element)) {
            throw new Error(`${role} added-element reference '${addedReference}' is not an element.`);
        }
        element = cell;
        if (element instanceof Node && Array.isArray(element.entityIds) && element.entityIds.length === 1) {
            entityId = element.entityIds[0];
        }
    }

    if (!isLiteralFlowEndpoint(element)) {
        throw new Error(`${role} cell '${String(element.id)}' cannot be a literal-flow endpoint.`);
    }
    return { element, entityId };
}

async function addLiteralFlow(
    ctx: BatchContext,
    mutation: Extract<MutationDescriptor, { type: 'addLiteralFlow' }>,
): Promise<joint.shapes.standard.Link> {
    const { graph } = ctx;
    if (mutation.flowType === FlowType.Miscellaneous) {
        throw new Error('Miscellaneous is a retired flow type and cannot be added.');
    }
    const supportedFlowTypes = [
        FlowType.Physical,
        FlowType.Legal,
        FlowType.Payment,
        FlowType.Service,
        FlowType.IPTransfer,
        FlowType.IPLicense,
        FlowType.Agreement,
        FlowType.Ownership,
    ];
    if (!supportedFlowTypes.includes(mutation.flowType)) {
        throw new Error(`Unsupported flow type '${String(mutation.flowType)}'.`);
    }

    const source = resolveLiteralEndpoint(ctx, mutation.source, 'source');
    const target = resolveLiteralEndpoint(ctx, mutation.target, 'target');
    const sameNode = source.element.id === target.element.id;
    if (sameNode && !(source.entityId && target.entityId && source.entityId !== target.entityId)) {
        throw new Error('Literal flows cannot connect a cell to itself.');
    }
    if (mutation.stepId && !(source.entityId && target.entityId)) {
        throw new Error('A step-owned literal flow requires entity-bearing endpoints.');
    }

    const link = createFlowLink(mutation.flowType);
    applyConnectorStyleMixin(link);
    if (mutation.stepId) {
        link.prop('stepId', mutation.stepId);
    }
    if (source.entityId) {
        link.prop('sourceEntityId', source.entityId);
    }
    if (target.entityId) {
        link.prop('targetEntityId', target.entityId);
    }
    link.source(source.element, { selector: 'body' });
    link.target(target.element, { selector: 'body' });
    if (sameNode) {
        link.prop('hiddenSameNodeLink', true);
        link.attr('root/display', 'none');
    }
    link.prop('agentAdded', true);
    graph.addCell(link);
    await applyFlowLinkPatch(link, mutation.patch);
    return link;
}

function upsertEntityNode(
    ctx: BatchContext,
    mutation: Extract<MutationDescriptor, { type: 'ensureEntityNode' }>,
): Node | undefined {
    const { graph } = ctx;
    const entityId = mutation.entityId;
    const shapeType = getShapeTypeByName(mutation.shape);
    const existingStandaloneNode = findStandaloneEntityNode(graph, entityId);

    if (existingStandaloneNode) {
        // ensureEntityNode acts as an upsert/refresh for existing standalone nodes so
        // classification, label, and jurisdiction visual state stay in sync with entity metadata.
        const template = createEntityNode(shapeType);
        if (existingStandaloneNode.get('type') === template.get('type')) {
            refreshEntityNode(existingStandaloneNode, mutation);
            // An explicit position moves the node and demotes its layout hint, so the returned
            // value below resolves to undefined: a placed node is never re-arranged.
            if (mutation.position) {
                repositionNode(existingStandaloneNode, mutation.position);
            }
            // If the node was previously created by AI tooling, return it so the mutation batch
            // can re-run layout when adding more AI-authored links between existing AI nodes. Step
            // projection reconciliation opts out: it is refreshing metadata, not adding geometry,
            // and must leave retained link vertices alone.
            return mutation.layoutExistingAgentNode !== false && existingStandaloneNode.prop('agentAdded') === true
                ? existingStandaloneNode
                : undefined;
        }
        // The node's shape must change (the entity's classification changed, or a single-member
        // group box is being redrawn as the entity itself). Rebuild the cell rather than morphing
        // its type/markup/attrs in place: a loaded cell keeps its original CLASS until the next
        // deserialisation, so its colour setters would keep writing to the old shape's selectors
        // (e.g. EntityGroup's setColor targets body1/body2) and the new markup's body would be
        // left showing the class-default fill from entity-nodes.ts.
        const replacement = replaceStandaloneNode(graph, existingStandaloneNode, template, mutation);
        return mutation.layoutExistingAgentNode !== false ? replacement : undefined;
    }

    // The entity is already drawn, but only inside a shared node (a group box or a multi-entity
    // card). Normally we leave it there (e.g. a flow endpoint). When forceStandalone is set this
    // is an explicit "add as an individual entity" request, so we create the standalone shape and
    // release the entity from its current host below — i.e. split it out.
    const hostNode = findEntityNode(graph, entityId);
    if (hostNode && !mutation.forceStandalone) {
        return undefined;
    }

    const node = createEntityNode(shapeType);
    refreshEntityNode(node, mutation);
    const autoPlaced = placeNode(node, mutation.position, graph);
    if (!mutation.diagramType) {
        node.resize(ENTITY_NODE_WIDTH, ENTITY_NODE_HEIGHT);
    }
    graph.addCell(node);

    if (hostNode) {
        // Split: this entity's flow links follow it onto the new standalone node, and whatever
        // hosted it — group box or shared multi-entity node — releases just this id (keeping its
        // other entities and their links; an emptied host's leftover links are rescued here).
        reattachEntityLinks(graph, entityId, node);
        releaseEntityFromNode(ctx, hostNode, entityId, node);
    }

    return autoPlaced ? node : undefined;
}

// Swaps a standalone node for a freshly built cell of the correct shape class, carrying over
// what identifies it in the graph (position, size, links, and the agentAdded layout hint) while
// label, colour, and entity state come from the mutation via refreshEntityNode. An explicit
// position in the mutation replaces the carried-over placement.
function replaceStandaloneNode(
    graph: joint.dia.Graph,
    oldNode: Node,
    replacement: Node,
    mutation: Extract<MutationDescriptor, { type: 'ensureEntityNode' }>,
): Node | undefined {
    refreshEntityNode(replacement, mutation);
    if (oldNode.prop('agentAdded') === true) {
        replacement.prop('agentAdded', true);
    }
    const position = oldNode.position();
    replacement.position(position.x, position.y);
    const size = oldNode.size();
    replacement.resize(size.width, size.height);
    if (mutation.position) {
        repositionNode(replacement, mutation.position);
    }
    graph.addCell(replacement);
    reattachEntityLinks(graph, mutation.entityId, replacement);
    moveConnectedLinks(graph, oldNode, replacement);
    graph.removeCells([oldNode]);
    return replacement.prop('agentAdded') === true ? replacement : undefined;
}

// Removes an entity from the diagram entirely: its attributed flow links are deleted and it is
// released from whatever node hosts it — a standalone node is removed with its links, while a
// group box or shared multi-entity node just drops the id (so no ghost membership lingers and no
// shared structure is deleted).
function removeEntity(ctx: BatchContext, entityId: string): void {
    const { graph } = ctx;
    const attributedLinks = graph.getLinks().filter(link =>
        String(link.prop('sourceEntityId') ?? '') === entityId
        || String(link.prop('targetEntityId') ?? '') === entityId,
    );
    graph.removeCells(attributedLinks);

    // Loop defensively: the one-node-per-entity invariant can be violated by legacy graphs.
    let node = findEntityNode(graph, entityId);
    while (node) {
        releaseEntityFromNode(ctx, node, entityId);
        node = findEntityNode(graph, entityId);
    }
}

interface FlowLinkUpsertOptions {
    allowUnattributedGroupMatch?: boolean;
}

function addFlowLinkToGraph(
    ctx: BatchContext,
    mutation: Extract<MutationDescriptor, { type: 'addFlowLink' }>,
    options: FlowLinkUpsertOptions = {},
): joint.dia.Link | undefined {
    const { graph } = ctx;
    const sourceNode = findEntityNode(graph, mutation.sourceEntityId);
    const targetNode = findEntityNode(graph, mutation.targetEntityId);
    if (!sourceNode || !targetNode) {
        return undefined;
    }

    const link = createFlowLink(mutation.flowType);
    const trimmedLabel = mutation.label?.trim();

    // Upsert rule: an explicit label updates an existing link in place; an omitted label leaves
    // its current text untouched.
    const existingLink = findExistingFlowLink(
        graph,
        mutation,
        String(sourceNode.id),
        String(targetNode.id),
        String(link.get('type')),
        options.allowUnattributedGroupMatch !== false,
    );
    if (existingLink) {
        if (trimmedLabel) {
            upsertLinkTextLabel(existingLink, trimmedLabel);
        }
        // A matched link missing attribution (editor-drawn, or backfilled on one side only) is
        // the same flow the agent is asserting: adopt the missing entity endpoint(s) so future
        // split/merge operations can follow them precisely.
        if (!existingLink.prop('sourceEntityId')) {
            existingLink.prop('sourceEntityId', mutation.sourceEntityId);
        }
        if (!existingLink.prop('targetEntityId')) {
            existingLink.prop('targetEntityId', mutation.targetEntityId);
        }
        return undefined;
    }

    applyConnectorStyleMixin(link);
    if (mutation.stepId) {
        link.prop('stepId', mutation.stepId);
    }
    // Record the *member* entity ids this flow runs between, independent of which visual node
    // currently hosts them. A flow to a grouped entity attaches to the group box, so the box-side
    // endpoint alone can't say which member it concerns; these props let split/merge/ungroup
    // reattach each link to the exact node that holds its endpoint entity (see reattachEntityLinks).
    link.prop('sourceEntityId', mutation.sourceEntityId);
    link.prop('targetEntityId', mutation.targetEntityId);
    if (trimmedLabel) {
        link.appendLabel(createFlowLinkTextLabel(trimmedLabel));
    }
    link.source(sourceNode, { selector: 'body' });
    link.target(targetNode, { selector: 'body' });
    link.prop('agentAdded', true);
    // Both endpoint entities may currently live in the same node (e.g. both grouped in one box).
    // The link is still added — attribution preserved — and the same-node visibility pass hides
    // it until a split/ungroup separates the endpoints again.
    graph.addCell(link);
    return link;
}

// Applies the desired graph projection for one Future State step without treating the graph as a
// disposable rendering. Exact semantic matches retain their user-authored presentation (labels,
// connector style, vertices, and id). A legacy group-level arrow is also a valid visual
// projection when it covers the complete desired member set for the same visual endpoints; it
// remains deliberately unattributed rather than being converted into one member link.
function reconcileStepProjection(
    ctx: BatchContext,
    mutation: Extract<MutationDescriptor, { type: 'reconcileStepProjection' }>,
): joint.dia.Link[] {
    const stepId = mutation.stepId.trim();
    if (!stepId) {
        throw new Error('A step projection requires a non-empty stepId.');
    }

    if (mutation.isOpaque) {
        if (mutation.desiredLinks.length > 0) {
            throw new Error(`Opaque step projection '${stepId}' cannot specify desired links.`);
        }
        return [];
    }

    // Map rather than a list: a compound move can produce the same tuple more than once, and the
    // legacy remove-and-add path collapsed that to one link through addFlowLink's upsert.
    const desiredByKey = new Map<string, StepProjectionLinkDescriptor>();
    for (const desired of mutation.desiredLinks) {
        if (!desired.sourceEntityId || !desired.targetEntityId) {
            throw new Error(`Step projection '${stepId}' requires source and target entity ids.`);
        }
        desiredByKey.set(stepProjectionKey(
            stepId,
            desired.sourceEntityId,
            desired.targetEntityId,
            getFlowTypeInfo(desired.flowType).linkClassName,
        ), desired);
    }

    // A manual fee drawn from a multi-entity group is one visual arrow, with no entity id on the
    // group side. It represents every member of that group only when every member tuple is still
    // desired. Give that group-level projection precedence over attributed member links: this
    // both preserves its presentation and repairs graphs produced by the old reconciler, which
    // added member arrows alongside it.
    const retainedGroupLinks = new Set<joint.dia.Link>();
    const groupCoveredKeys = new Set<string>();
    const retainedGroupVisualKeys = new Set<string>();
    for (const link of ctx.graph.getLinks()) {
        if (!stepIdsEqual(link.prop('stepId'), stepId)) {
            continue;
        }

        const coverage = groupProjectionCoverage(ctx, link, stepId, desiredByKey);
        if (!coverage) {
            continue;
        }

        const visualKey = groupProjectionVisualKey(link);
        if (retainedGroupVisualKeys.has(visualKey)) {
            continue;
        }

        retainedGroupVisualKeys.add(visualKey);
        retainedGroupLinks.add(link);
        for (const key of coverage) {
            groupCoveredKeys.add(key);
        }
    }

    const addedLinks: joint.dia.Link[] = [];
    for (const [key, desired] of desiredByKey) {
        if (groupCoveredKeys.has(key)) {
            continue;
        }

        const labelPatch = resolveProjectionLabelPatch(desired.label);

        const added = addFlowLinkToGraph(
            ctx,
            {
                type: 'addFlowLink',
                sourceEntityId: desired.sourceEntityId,
                targetEntityId: desired.targetEntityId,
                flowType: desired.flowType,
                stepId,
                label: labelPatch.kind === 'set' ? labelPatch.value : undefined,
            },
            // An unattributed multi-member endpoint is a group-level visual projection, not a
            // fallback match for one arbitrary member. It was either retained above or is stale
            // and must be replaced by an exactly-attributed new arrow.
            { allowUnattributedGroupMatch: false },
        );
        if (added) {
            addedLinks.push(added);
        }

        if (labelPatch.kind === 'clear') {
            const targetLink = added ?? ctx.graph.getLinks()
                .find(link => stepProjectionKeyForLink(link) === key);
            if (targetLink) {
                clearFirstLinkTextLabel(targetLink);
            }
        }
    }

    // Exact tuple identity is the only presentation-preserving match. The retained group links
    // above are the one explicit visual-projection exception; every stale or duplicate link is
    // removed, including its label, rather than lending presentation to a new endpoint pair.
    const retainedKeys = new Set<string>();
    const linksToRemove = ctx.graph.getLinks().filter(link => {
        if (!stepIdsEqual(link.prop('stepId'), stepId)) {
            return false;
        }

        if (retainedGroupLinks.has(link)) {
            return false;
        }

        const key = stepProjectionKeyForLink(link);
        if (!key || !desiredByKey.has(key) || groupCoveredKeys.has(key) || retainedKeys.has(key)) {
            return true;
        }

        retainedKeys.add(key);
        return false;
    });
    ctx.graph.removeCells(linksToRemove);
    return addedLinks;
}

function groupProjectionCoverage(
    ctx: BatchContext,
    link: joint.dia.Link,
    stepId: string,
    desiredByKey: ReadonlyMap<string, StepProjectionLinkDescriptor>,
): string[] | undefined {
    if (!hasUnattributedGroupEndpoint(link, 'source') && !hasUnattributedGroupEndpoint(link, 'target')) {
        return undefined;
    }

    const sourceMembers = projectionEndpointEntityIds(link, 'source');
    const targetMembers = projectionEndpointEntityIds(link, 'target');
    const sourceEndId = getLinkEndId(link, 'source');
    const targetEndId = getLinkEndId(link, 'target');
    if (!sourceMembers || !targetMembers || !sourceEndId || !targetEndId) {
        return undefined;
    }

    const linkType = String(link.get('type') ?? '');
    const coverage = new Set<string>();
    for (const sourceEntityId of sourceMembers) {
        for (const targetEntityId of targetMembers) {
            const key = stepProjectionKey(stepId, sourceEntityId, targetEntityId, linkType);
            const desired = desiredByKey.get(key);
            if (!desired) {
                // A group arrow cannot stand for a subset of its member pairs: that would make a
                // removed payer/payee look still connected through the group box.
                return undefined;
            }

            const sourceNode = findEntityNode(ctx.graph, desired.sourceEntityId);
            const targetNode = findEntityNode(ctx.graph, desired.targetEntityId);
            if (!sourceNode || !targetNode
                || String(sourceNode.id) !== sourceEndId
                || String(targetNode.id) !== targetEndId) {
                return undefined;
            }

            coverage.add(key);
        }
    }

    return coverage.size > 0 ? [...coverage] : undefined;
}

function projectionEndpointEntityIds(link: joint.dia.Link, end: 'source' | 'target'): string[] | undefined {
    const attribution = link.prop(end === 'source' ? 'sourceEntityId' : 'targetEntityId');
    if (typeof attribution === 'string' && attribution.length > 0) {
        return [attribution];
    }

    const element = end === 'source' ? link.getSourceElement() : link.getTargetElement();
    if (!(element instanceof Node) || !Array.isArray(element.entityIds) || element.entityIds.length === 0) {
        return undefined;
    }
    return element.entityIds;
}

function groupProjectionVisualKey(link: joint.dia.Link): string {
    return `${getLinkEndId(link, 'source') ?? ''}\u0000${getLinkEndId(link, 'target') ?? ''}\u0000${String(link.get('type') ?? '')}`;
}

function stepProjectionKey(
    stepId: string,
    sourceEntityId: string,
    targetEntityId: string,
    linkClassName: string,
): string {
    return `${stepId.toLowerCase()}\u0000${sourceEntityId}\u0000${targetEntityId}\u0000${linkClassName}`;
}

function stepProjectionKeyForLink(link: joint.dia.Link): string | undefined {
    const stepId = link.prop('stepId');
    const sourceEntityId = link.prop('sourceEntityId');
    const targetEntityId = link.prop('targetEntityId');
    if (typeof stepId !== 'string' || typeof sourceEntityId !== 'string' || typeof targetEntityId !== 'string') {
        return undefined;
    }
    return stepProjectionKey(stepId, sourceEntityId, targetEntityId, String(link.get('type') ?? ''));
}

function stepIdsEqual(left: unknown, right: string): boolean {
    return typeof left === 'string' && left.toLowerCase() === right.toLowerCase();
}

type ProjectionLabelPatch =
    | { kind: 'unchanged' }
    | { kind: 'clear' }
    | { kind: 'set'; value: string };

function resolveProjectionLabelPatch(patch: PatchValue<string> | undefined): ProjectionLabelPatch {
    if (patch === undefined) {
        return { kind: 'unchanged' };
    }
    if (patch.clear === true) {
        if (patch.value !== undefined && patch.value !== null) {
            throw new Error('A step projection label cannot specify both clear and value.');
        }
        return { kind: 'clear' };
    }
    if (typeof patch.value !== 'string' || !patch.value.trim()) {
        throw new Error('A step projection label must specify non-empty text or clear.');
    }
    return { kind: 'set', value: patch.value.trim() };
}

// Finds an existing link representing the same flow, so addFlowLink can update it in place rather
// than stacking a duplicate arrow. Identity is decided per endpoint: an attributed endpoint must
// match by entity id — so two flows between different members that currently resolve to the same
// node pair (both grouped) are distinct flows and never collapse into one — while an endpoint
// that predates attribution falls back to node identity. stepId must match in all cases:
// current-state links carry none (both undefined → matched), while future-state links are
// per-step, so links from different steps are never treated as the same.
function findExistingFlowLink(
    graph: joint.dia.Graph,
    flow: { sourceEntityId: string; targetEntityId: string; stepId?: string },
    sourceNodeId: string,
    targetNodeId: string,
    linkType: string,
    allowUnattributedGroupMatch: boolean,
): joint.dia.Link | undefined {
    return graph.getLinks().find(link => {
        if (String(link.get('type')) !== linkType) {
            return false;
        }
        const rawLinkStepId = link.prop('stepId');
        const linkStepId = typeof rawLinkStepId === 'string' ? rawLinkStepId : undefined;
        const flowStepId = flow.stepId;
        if (linkStepId !== undefined || flowStepId !== undefined) {
            if (linkStepId === undefined || flowStepId === undefined || !stepIdsEqual(linkStepId, flowStepId)) {
                return false;
            }
        }
        if (linkStepId === undefined && flowStepId === undefined && rawLinkStepId != null) {
            return false;
        }
        const linkSourceEntityId = link.prop('sourceEntityId');
        const linkTargetEntityId = link.prop('targetEntityId');
        if (!allowUnattributedGroupMatch
            && (hasUnattributedGroupEndpoint(link, 'source') || hasUnattributedGroupEndpoint(link, 'target'))) {
            return false;
        }
        const sourceMatches = linkSourceEntityId
            ? String(linkSourceEntityId) === flow.sourceEntityId
            : getLinkEndId(link, 'source') === sourceNodeId;
        const targetMatches = linkTargetEntityId
            ? String(linkTargetEntityId) === flow.targetEntityId
            : getLinkEndId(link, 'target') === targetNodeId;
        return sourceMatches && targetMatches;
    });
}

function hasUnattributedGroupEndpoint(link: joint.dia.Link, end: 'source' | 'target'): boolean {
    const attribution = link.prop(end === 'source' ? 'sourceEntityId' : 'targetEntityId');
    if (typeof attribution === 'string' && attribution.length > 0) {
        return false;
    }

    const element = end === 'source' ? link.getSourceElement() : link.getTargetElement();
    return element instanceof Node && Array.isArray(element.entityIds) && element.entityIds.length > 1;
}

// Creates (or upserts) a single EntityGroup box that represents the member entities.
// No individual member cards are drawn: the box itself is the flow-targetable node and carries
// every member entity id, so a flow referencing any member attaches to this one box.
function createEntityGroup(
    ctx: BatchContext,
    mutation: Extract<MutationDescriptor, { type: 'createEntityGroupWithIndividuals' }>,
): Node | undefined {
    const { graph, catalog } = ctx;

    // De-duplicate members within the request itself (an entity may not appear twice in one group).
    const entityIds: string[] = [];
    const seen = new Set<string>();
    for (const entity of mutation.entities) {
        if (!seen.has(entity.entityId)) {
            seen.add(entity.entityId);
            entityIds.push(entity.entityId);
        }
    }
    if (entityIds.length === 0) {
        return undefined;
    }

    // Upsert onto an engine-created box (stable groupId) when one relates to this member set, so
    // re-grouping mutates that box in place — preserving its id and position — instead of
    // destroying and recreating it. User-created boxes are never adopted or overwritten.
    let groupNode = findUpsertableGroupBox(graph, entityIds);
    const isNewBox = groupNode === undefined;
    let autoPlaced = false;
    if (!groupNode) {
        groupNode = createEntityNode(ShapeType.Group);
        groupNode.prop('groupId', joint.util.uuid());
        groupNode.resize(GROUP_NODE_WIDTH, GROUP_NODE_HEIGHT);
        autoPlaced = placeNode(groupNode, mutation.position, graph);
        graph.addCell(groupNode);
    } else {
        // Members the reused box holds that are NOT in the new set are being implicitly
        // ungrouped: materialise each as a standalone node (with its links) so no entity
        // silently disappears from the diagram.
        const target = new Set(entityIds);
        const boxIds: string[] = Array.isArray(groupNode.entityIds) ? groupNode.entityIds : [];
        const boxPosition = groupNode.position();
        for (const droppedId of boxIds.filter(id => !target.has(id))) {
            const node = buildStandaloneNodeForMember(ctx, groupNode, droppedId);
            const position = findAvailablePosition(graph, boxPosition.x, boxPosition.y);
            node.position(position.x, position.y);
            graph.addCell(node);
            reattachEntityLinks(graph, droppedId, node);
        }
    }
    ctx.touchedBoxIds.add(String(groupNode.id));
    if (!isNewBox && mutation.position) {
        // Re-grouping onto an existing box normally preserves its placement; an explicit position
        // overrides that and takes the box out of automatic layout.
        repositionNode(groupNode, mutation.position);
    }

    // { rewrite: true }: joint's prop() merges arrays by index, so a reused box whose member set
    // shrank would keep stale trailing ids without it.
    groupNode.prop('entityIds', entityIds, { rewrite: true });
    // Prefer the caller-supplied label; fall back to an auto label (first member "+ N more").
    // The custom label is persisted on the box so it survives member release/re-labelling. When no
    // label is supplied (null/undefined), keep whatever label the box already carries — re-grouping
    // to add or drop a member must not wipe a name the user gave the box.
    const existingLabel = (groupNode.prop('groupLabel') as string | undefined)?.trim() ?? '';
    const trimmedLabel = mutation.groupLabel?.trim() ?? existingLabel;
    groupNode.prop('groupLabel', trimmedLabel, { rewrite: true });
    const memberEntries = entityIds.map(id => catalog.get(id));
    if (trimmedLabel) {
        groupNode.setLabel(trimmedLabel);
    } else if (memberEntries[0]) {
        groupNode.setLabel(buildGroupLabel(memberEntries[0].label, entityIds.length));
    }
    // Colour reflects the members: a group spanning US and non-US members shows as Mixed. When a
    // member is unknown to the catalog the existing colours are kept rather than guessed.
    if (memberEntries.every(entry => entry !== undefined)) {
        groupNode.setColorByJurisdiction(
            getGroupJurisdiction(memberEntries.map(entry => (entry as EntityCatalogEntry).countryCode)),
        );
    }

    // Explicit merge: pull every member into this box. Its flow links move onto the box (by the
    // links' recorded endpoint entity ids) and whatever node previously hosted it — a standalone
    // shape, a shared multi-entity node, or a different group box — releases it, so each entity
    // ends up in exactly this one node.
    const linksByEntityId = indexLinksByEntityId(graph);
    const elementsByEntityId = indexElementsByEntityId(graph);
    for (const entityId of entityIds) {
        reattachEntityLinks(graph, entityId, groupNode, linksByEntityId.get(entityId) ?? []);
        absorbEntityIntoGroup(ctx, groupNode, entityId, elementsByEntityId.get(entityId) ?? []);
    }

    // Only a newly created, auto-placed box joins layout; a reused box — or one the caller
    // positioned explicitly — keeps its position by not triggering a re-layout on its own.
    return autoPlaced ? groupNode : undefined;
}

// Finds the engine-created group box to upsert a grouping request onto. Only boxes carrying a
// groupId (created by this engine, or backfilled from agent-created boxes) are candidates: a
// user-created group box is never adopted — its members may be absorbed out individually, but the
// box itself is not rewritten. An exact member-set match wins; otherwise the candidate sharing
// the most members with the request is reused in place, which is what keeps a box's identity and
// position stable across regroupings ({A,B} → {A,B,C} mutates the same box).
function findUpsertableGroupBox(graph: joint.dia.Graph, entityIds: string[]): Node | undefined {
    const target = new Set(entityIds);
    let best: Node | undefined;
    let bestOverlap = 0;
    for (const element of graph.getElements()) {
        if (!(element instanceof Node) || element.get('type') !== 'EntityGroup') {
            continue;
        }
        const groupId = element.prop('groupId');
        if (typeof groupId !== 'string' || groupId.length === 0) {
            continue;
        }
        const ids: string[] = Array.isArray(element.entityIds) ? element.entityIds : [];
        const overlap = ids.filter(id => target.has(id)).length;
        if (overlap === target.size && ids.length === target.size) {
            return element;
        }
        if (overlap > bestOverlap) {
            bestOverlap = overlap;
            best = element;
        }
    }
    return best;
}

// Removes every *other* representation of `entityId` so the group box is its sole node. Each host
// releases just this entity: standalone nodes are removed (their remaining unattributed links
// rescued onto the box), while shared nodes — other group boxes or multi-entity cards — keep
// their other entities and links. Called after reattachEntityLinks has already moved the entity's
// attributed links onto `groupNode`.
function absorbEntityIntoGroup(
    ctx: BatchContext,
    groupNode: Node,
    entityId: string,
    candidateElements: joint.dia.Element[] = ctx.graph.getElements(),
): void {
    const groupId = String(groupNode.id);
    for (const element of [...candidateElements]) {
        if (!(element instanceof Node)
            || String(element.id) === groupId
            || !Array.isArray(element.entityIds)
            || !element.entityIds.includes(entityId)
            // guard against a stale candidate index: skip elements no longer in the graph
            || ctx.graph.getCell(String(element.id)) !== element) {
            continue;
        }
        releaseEntityFromNode(ctx, element, entityId, groupNode);
    }
}

function indexElementsByEntityId(graph: joint.dia.Graph): Map<string, Node[]> {
    const index = new Map<string, Node[]>();
    for (const element of graph.getElements()) {
        if (!(element instanceof Node) || !Array.isArray(element.entityIds)) {
            continue;
        }
        for (const entityId of element.entityIds) {
            const list = index.get(entityId);
            if (list) {
                list.push(element);
            } else {
                index.set(entityId, [element]);
            }
        }
    }
    return index;
}

// ─── Group box derivation (catalog-driven) ────────────────────────────────────

function relabelGroupBox(ctx: BatchContext, box: Node): void {
    if (box.get('type') !== 'EntityGroup') {
        return;
    }
    // A box the engine did not create (no stable groupId) keeps its user-authored label and
    // colours untouched: only its membership changes when entities are absorbed out of it.
    if (typeof box.prop('groupId') !== 'string') {
        return;
    }
    const ids: string[] = Array.isArray(box.entityIds) ? box.entityIds : [];
    const memberEntries = ids.map(id => ctx.catalog.get(id));
    // Keep a custom label if one was supplied; otherwise rebuild the auto label from the members.
    // When the first member is unknown to the catalog the current label is kept — the engine
    // never fabricates a raw-id label.
    const customLabel = (box.prop('groupLabel') as string | undefined)?.trim();
    if (customLabel) {
        box.setLabel(customLabel);
    } else if (memberEntries[0]) {
        box.setLabel(buildGroupLabel(memberEntries[0].label, ids.length));
    }
    // Re-derive the box colour: dropping a member can turn a Mixed group into a single-jurisdiction
    // one. If any member is unknown to the catalog, the existing colours are kept rather than guessed.
    if (memberEntries.every(entry => entry !== undefined)) {
        box.setColorByJurisdiction(
            getGroupJurisdiction(memberEntries.map(entry => (entry as EntityCatalogEntry).countryCode)),
        );
    }
}

// Replaces any batch-touched EntityGroup box that is left with a single member by an individual
// entity shape for that member (correct shape + jurisdiction colour from the catalog), preserving
// its flow links. A single-member box is just the 1-member case of an ungroup.
function normalizeSingleMemberGroups(ctx: BatchContext): void {
    for (const boxId of ctx.touchedBoxIds) {
        const cell = ctx.graph.getCell(boxId);
        if (!(cell instanceof Node) || cell.get('type') !== 'EntityGroup') {
            continue;
        }
        const ids: string[] = Array.isArray(cell.entityIds) ? cell.entityIds : [];
        if (ids.length === 1) {
            explodeGroupBox(ctx, cell);
        } else if (ids.length === 0) {
            // Defensive: releases remove emptied boxes eagerly, so this should not occur.
            removeNodeAndConnectedLinks(ctx.graph, cell);
        }
    }
}

// Ungroups an EntityGroup box: redraws every member as its own standalone shape (resolved from
// the catalog) and moves each of the box's flow links onto the member it belongs to (by the
// link's recorded endpoint entity ids), then removes the box.
function explodeGroupBox(ctx: BatchContext, box: Node): Node[] {
    const { graph } = ctx;
    const ids: string[] = Array.isArray(box.entityIds) ? [...box.entityIds] : [];
    const position = box.position();

    const created: Node[] = [];
    for (const entityId of ids) {
        const node = buildStandaloneNodeForMember(ctx, box, entityId);
        node.position(position.x, position.y);
        graph.addCell(node);
        // Move exactly this member's flow links onto its new standalone node.
        reattachEntityLinks(graph, entityId, node);
        created.push(node);
    }

    // Any links still on the box predate per-link entity attribution (no recorded endpoint entity);
    // move them to the first member so no arrow is silently dropped, then remove the link-free box.
    if (created.length > 0) {
        moveConnectedLinks(graph, box, created[0]);
    }
    removeNodeAndConnectedLinks(graph, box);
    return created;
}

// Builds (without placing) the standalone node for a group-box member. Catalog-first: a known
// entity gets its proper shape, label, and jurisdiction colour. An id the catalog does not know
// keeps the box's existing visuals — label and colour are carried over rather than fabricating a
// raw-id label or a default jurisdiction colour.
function buildStandaloneNodeForMember(ctx: BatchContext, box: Node, entityId: string): Node {
    const entry = ctx.catalog.get(entityId);
    if (entry) {
        return buildStandaloneEntityNode(entry);
    }
    const node = createEntityNode(ShapeType.CorporationCFC);
    node.entityIds = [entityId];
    const boxLabel = String(box.attr('label/text') ?? '').replace(/\n/g, ' ').trim();
    if (boxLabel) {
        node.setLabel(boxLabel);
    }
    // EntityGroup keeps its colour on the stacked body1/body2 rects (body is transparent).
    const boxColor = box.get('type') === 'EntityGroup'
        ? String(box.attr('body1/fill') ?? '')
        : box.getColor();
    if (boxColor && boxColor !== 'transparent') {
        node.setColor(boxColor);
    }
    node.resize(ENTITY_NODE_WIDTH, ENTITY_NODE_HEIGHT);
    return node;
}

// Builds (without placing) a standalone individual-entity node from its catalog entry. Position
// and graph.addCell are left to the caller. Not stamped agentAdded: these nodes replace an
// existing box in place (keeping its position), they are not new batch additions to lay out.
function buildStandaloneEntityNode(entry: EntityCatalogEntry): Node {
    const node = createEntityNode(getShapeTypeByName(entry.shape));
    node.entityIds = [entry.entityId];
    node.setLabel(entry.label);
    node.setColorByJurisdiction(getJurisdictionForCountryCode(entry.countryCode));
    node.resize(ENTITY_NODE_WIDTH, ENTITY_NODE_HEIGHT);
    return node;
}

// Group box label: the first member's name plus a count of the rest, e.g. "Acme Corp + 2 more".
// A single-member group just shows that member's name.
function buildGroupLabel(firstName: string, memberCount: number): string {
    const remaining = memberCount - 1;
    return remaining > 0 ? `${firstName} + ${remaining} more` : (firstName ?? '');
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function serializeGraph(graph: joint.dia.Graph): string {
    return JSON.stringify(graph.toJSON());
}

function findEntityNode(graph: joint.dia.Graph, entityId: string): Node | undefined {
    return graph.getElements().find(element =>
        element instanceof Node
        && Array.isArray(element.entityIds)
        && element.entityIds.includes(entityId),
    ) as Node | undefined;
}

// Sets the link's text label, replacing the existing text label's text if one is present
// (preserving its position/styling) and leaving any icon labels untouched.
function upsertLinkTextLabel(link: joint.dia.Link, text: string): void {
    setFirstLinkTextLabel(link, text, true);
}

function clearFirstLinkTextLabel(link: joint.dia.Link): void {
    setFirstLinkTextLabel(link, '', false);
}

function setFirstLinkTextLabel(link: joint.dia.Link, text: string, appendIfMissing: boolean): void {
    const labels = link.labels() ?? [];
    const textLabelIndex = labels.findIndex(label => !isIconLabel(label));
    if (textLabelIndex >= 0) {
        updateLinkTextLabel(link, textLabelIndex, text);
    } else if (appendIfMissing) {
        link.appendLabel(createFlowLinkTextLabel(text));
    }
}

function findStandaloneEntityNode(graph: joint.dia.Graph, entityId: string): Node | undefined {
    return graph.getElements().find(element =>
        element instanceof Node
        && Array.isArray(element.entityIds)
        && element.entityIds.length === 1
        && element.entityIds[0] === entityId,
    ) as Node | undefined;
}

interface LayoutTracking {
    addedNodeIds: Set<string>;
    addedLinkIds: Set<string>;
}

function createLayoutTracking(): LayoutTracking {
    // Track only cells created by this mutation batch so layout impact stays local.
    return {
        addedNodeIds: new Set<string>(),
        addedLinkIds: new Set<string>(),
    };
}

function applyMsaglLayout(graph: joint.dia.Graph, tracking: LayoutTracking): void {
    // We only auto-layout when at least one new node is introduced.
    if (tracking.addedNodeIds.size === 0) {
        return;
    }

    // Expand scope to all agent-added nodes so that nodes from consecutive agent batches
    // are positioned holistically rather than each batch optimising in isolation.
    const allAgentNodeIds = new Set<string>(
        graph.getElements()
            .filter(element => element.prop('agentAdded') === true)
            .map(element => String(element.id)),
    );

    // Only include agent-to-agent cells in the layout scope. Keeping the scope tight means
    // MSAGL positions everything in its own origin-relative coordinate space, and we
    // can translate the entire cluster — nodes *and* link vertices — by exactly the same delta,
    // preserving the routing that MSAGL computed without any desynchronisation.
    const agentCells = collectAgentCells(graph, allAgentNodeIds);
    if (agentCells.length === 0) {
        return;
    }

    const layerDirection: LayerDirectionEnum = LayerDirectionEnum.LR;

    try {
        // Layout defaults:
        // - nodeSeparation: gap between sibling nodes on the same layer.
        // - layerSeparation: gap between layers (in LR mode, this is left-to-right spacing).
        // - edgeRoutingMode: rectilinear routes orthogonal link segments.
        // - setVertices: persist the routed points onto link vertices.
        // - setLabels: persist the routed label positions onto label attributes.
        // - setAnchor: false — the perpendicular anchor and boundary connectionPoint are applied
        //   after layout by finalizeLaidOutLinkGeometry.  MSAGL routing is unaffected; the callback
        //   would only have written results to the link model after routing was already computed.
        msaglLayout(agentCells, {
            layerDirection,
            nodeSeparation: 70,
            layerSeparation: 120,
            edgeRoutingMode: EdgeRoutingMode.Rectilinear,
            setVertices: true,
            setLabels: true,
            setAnchor: false,
            x: 0,
            y: 0,
        });

        // Apply perpendicular anchor and boundary connectionPoint to each laid-out link so that:
        //   • Parallel edges keep MSAGL's computed spacing (perpendicular aligns with each edge's
        //     first/last vertex y-position, producing different connection points per edge).
        //   • Terminal segments are always orthogonal.
        //   • Link ends connect to the actual shape boundary, not the bbox rectangle.
        //   • The Segments tool can move connection points freely between sides (perpendicular
        //     is dynamic — it slides when vertices change, unlike a fixed offset anchor).
        finalizeLaidOutLinkGeometry(agentCells);

        // Apply one uniform translation to the entire laid-out subgraph so node/link geometry
        // remains internally consistent and we avoid per-link or per-vertex adjustment logic.
        const translation = computeClusterTranslation(graph, allAgentNodeIds, agentCells);
        translateSubgraph(graph, agentCells, translation);
    } catch {
        // Keep persisted graph stable if the layout plugin cannot process the current graph subset.
    }
}

// Returns only the agent nodes and the links that run between them.
// Links that connect an agent node to a user-placed node are excluded so that
// MSAGL operates on a self-contained subgraph whose output is a clean
// origin-relative layout that can be uniformly translated into graph space.
// Same-node (hidden) links are excluded — they have no geometry to route.
function collectAgentCells(graph: joint.dia.Graph, agentNodeIds: Set<string>): joint.dia.Cell[] {
    const cellIds = new Set<string>(agentNodeIds);

    for (const nodeId of agentNodeIds) {
        const node = graph.getCell(nodeId);
        if (!(node instanceof joint.dia.Element)) {
            continue;
        }

        for (const link of graph.getConnectedLinks(node)) {
            const sourceId = getLinkEndId(link, 'source');
            const targetId = getLinkEndId(link, 'target');
            if (sourceId && agentNodeIds.has(sourceId)
                && targetId && agentNodeIds.has(targetId)
                && sourceId !== targetId) {
                cellIds.add(String(link.id));
            }
        }
    }

    return [...cellIds].map(id => graph.getCell(id)).filter(Boolean) as joint.dia.Cell[];
}

function finalizeLaidOutLinkGeometry(cells: joint.dia.Cell[]): void {
    for (const cell of cells) {
        if (!(cell instanceof joint.dia.Link)) {
            continue;
        }

        for (const endType of ['source', 'target'] as const) {
            // 'perpendicular' anchor slides along the element's horizontal and vertical
            // centrelines to keep the terminal segment orthogonal.  Critically, it is
            // dynamic: it recalculates whenever the adjacent vertex moves, so dragging
            // Segments tool handles causes the connection point to float freely to the
            // nearest appropriate side rather than appearing fixed.
            //
            // For parallel edges, MSAGL places their first/last vertices at different
            // y-positions.  The perpendicular anchor aligns with each vertex's y-coordinate
            // on the vertical centreline, reproducing MSAGL's intended spacing without
            // storing a hard offset.
            cell.prop(`${endType}/anchor`, {
                name: 'perpendicular',
                args: { useModelGeometry: true },
            });

            // 'boundary' connectionPoint finds the intersection with the actual shape
            // boundary (triangle polygon, ellipse path, etc.) so link ends visually
            // connect to the real outline, not the bounding-box rectangle.
            cell.prop(`${endType}/connectionPoint`, {
                name: 'boundary',
                args: {
                    selector: 'body',
                    sticky: true,
                    extrapolate: true,
                    stroke: true,
                },
            });

            // Ensure the body selector is set for element-connected ends.
            const endDefinition = cell.get(endType);
            if (endDefinition && typeof endDefinition === 'object') {
                const id = (endDefinition as { id?: unknown }).id;
                const selector = (endDefinition as { selector?: unknown }).selector;
                if ((typeof id === 'string' || typeof id === 'number') && selector == null) {
                    cell.prop(`${endType}/selector`, 'body');
                }
            }
        }
    }
}

// Computes a translation vector that moves the agent cluster (whose bounding box starts at the
// layout origin) to a sensible position in the existing graph:
// - If the agent nodes have non-agent neighbours, the cluster is placed to the right of the
//   rightmost neighbour, vertically centred on those neighbours.
// - Otherwise the cluster is placed to the right of all existing nodes (or at a fixed offset
//   when the graph is empty).
function computeClusterTranslation(
    graph: joint.dia.Graph,
    agentNodeIds: Set<string>,
    agentCells: joint.dia.Cell[],
): { x: number; y: number } {
    const clusterBBox = graph.getCellsBBox(agentCells);
    if (!clusterBBox) {
        return { x: 0, y: 0 };
    }

    // Collect non-agent elements that are directly connected to any agent node.
    const neighborSet = new Set<string>();
    for (const nodeId of agentNodeIds) {
        const node = graph.getCell(nodeId);
        if (!(node instanceof joint.dia.Element)) {
            continue;
        }
        for (const link of graph.getConnectedLinks(node)) {
            for (const end of ['source', 'target'] as const) {
                const endId = getLinkEndId(link, end);
                if (endId && !agentNodeIds.has(endId)) {
                    neighborSet.add(endId);
                }
            }
        }
    }

    const neighborElements = [...neighborSet]
        .map(id => graph.getCell(id))
        .filter((c): c is joint.dia.Element => c instanceof joint.dia.Element);

    let targetX: number;
    let targetY: number;

    if (neighborElements.length > 0) {
        const neighborRight = Math.max(
            ...neighborElements.map(e => e.position().x + (Number(e.size().width) || 0)),
        );
        const neighborCenterY = neighborElements.reduce(
            (sum, e) => sum + e.position().y + (Number(e.size().height) || 0) / 2,
            0,
        ) / neighborElements.length;

        targetX = neighborRight + 120;
        targetY = neighborCenterY - clusterBBox.height / 2;
    } else {
        const nonAgentElements = graph.getElements().filter(e => !agentNodeIds.has(String(e.id)));
        if (nonAgentElements.length > 0) {
            const rightmost = Math.max(
                ...nonAgentElements.map(e => e.position().x + (Number(e.size().width) || 0)),
            );
            const topmost = Math.min(...nonAgentElements.map(e => e.position().y));
            targetX = rightmost + 120;
            targetY = topmost;
        } else {
            targetX = 100;
            targetY = 100;
        }
    }

    return { x: targetX - clusterBBox.x, y: targetY - clusterBBox.y };
}

function translateSubgraph(
    graph: joint.dia.Graph,
    cells: joint.dia.Cell[],
    translation: { x: number; y: number },
): void {
    if (translation.x === 0 && translation.y === 0) {
        return;
    }

    graph.startBatch('agent-subgraph-translate');
    for (const cell of cells) {
        if (cell instanceof joint.dia.Element || cell instanceof joint.dia.Link) {
            cell.translate(translation.x, translation.y);
        }
    }
    graph.stopBatch('agent-subgraph-translate');
}

function refreshEntityNode(
    node: Node,
    mutation: Extract<MutationDescriptor, { type: 'ensureEntityNode' }>,
): void {
    node.entityIds = [mutation.entityId];
    node.isNewEntity = mutation.isNewEntity;

    if (mutation.diagramType) {
        const shapeType = getShapeTypeByName(mutation.shape);
        const isUs = String(mutation.countryCode || '').toUpperCase() === 'US';
        const jurisdiction = mutation.isExternal === true
            ? Jurisdiction.ThirdParty
            : (isUs ? Jurisdiction.US : Jurisdiction.NonUS);

        const options: EntityNodeOptions = {
            entityId: mutation.entityId,
            entityIds: [mutation.entityId],
            label: mutation.label,
            countryCode: mutation.countryCode,
            country: mutation.country || mutation.countryCode,
            diagramType: mutation.diagramType,
            shape: shapeType,
            jurisdiction: jurisdiction,
            isNewEntity: mutation.isNewEntity ?? false,
        };

        applyOrgChartStylingToNode(node, options);
    } else {
        node.setLabel(mutation.label);
        node.setColorByJurisdiction(
            mutation.isExternal === true
                ? Jurisdiction.ThirdParty
                : getJurisdictionForCountryCode(mutation.countryCode),
        );
    }
}

function getDefaultEntityPlacement(graph: joint.dia.Graph): { x: number; y: number } {
    return findAvailablePosition(graph, 0, 0);
}

function findAvailablePosition(
    graph: joint.dia.Graph,
    startX: number,
    startY: number,
    offsetStep: number = 45,
): { x: number; y: number } {
    let x = startX;
    let y = startY;

    const isOccupied = (candidateX: number, candidateY: number) =>
        graph.getElements().some(element => {
            const position = element.position();
            return position.x === candidateX && position.y === candidateY;
        });

    while (isOccupied(x, y)) {
        x += offsetStep;
        y += offsetStep;
    }

    return { x, y };
}

function getJurisdictionForCountryCode(countryCode: string | null | undefined): Jurisdiction {
    return countryCode?.toUpperCase() === 'US'
        ? Jurisdiction.US
        : Jurisdiction.NonUS;
}

// A group box reflects the jurisdictions of its members: all US → US, all non-US → NonUS, and a
// mix of both → Mixed (so the EntityGroup shape shows its two stacked rectangles in the US and
// non-US colours instead of being coloured by an arbitrary "first member" country).
function getGroupJurisdiction(countryCodes: readonly string[]): Jurisdiction {
    let hasUs = false;
    let hasNonUs = false;
    for (const code of countryCodes) {
        if (getJurisdictionForCountryCode(code) === Jurisdiction.US) {
            hasUs = true;
        } else {
            hasNonUs = true;
        }
    }
    if (hasUs && hasNonUs) {
        return Jurisdiction.Mixed;
    }
    return hasNonUs ? Jurisdiction.NonUS : Jurisdiction.US;
}

// Converts a canonical shape name (sent by the C# EntityShapeResolver, whose names match the
// ShapeType enum members on both sides) to the ShapeType enum. The classification→shape decision
// lives solely in C#; this lookup is mechanical and automatically covers new enum members.
function getShapeTypeByName(shape: string | null | undefined): ShapeType {
    const mapped = shape ? ShapeType[shape as keyof typeof ShapeType] : undefined;
    return typeof mapped === 'number' ? mapped : ShapeType.CorporationCFC;
}
